'use client';

import * as React from 'react';
import { Search } from 'lucide-react';

import { GlassControl } from '@/components/atoms/GlassControl';
import { cn } from '@/components/atoms/primitives/utils';
import { useSearchSheet } from '@/hooks/useSearchSheet';
import { useIsMacPlatform } from '@/hooks/useGlobalSearchShortcut';
import { formatSearchCityPlaceholder } from '@/lib/copy/searchCityCopy';

export interface CityHeaderSearchProps {
  /** City display name shown in the pill placeholder ("Search Charlotte…"). */
  cityName: string;
  className?: string;
}

// DS 1.9.0 `o-globalnav__search` — the borderless glass pill rendered by the
// `GlassControl` atom at the nav's own size (WEB-1021, operator ruling
// 2026-09-29): 36 tall, pill radius, `padding: 0 7px 0 13px`, gap 10, a search
// glyph, a left-aligned "Search <city>…" placeholder, and a mono `⌘K` /
// `Ctrl K` key cap. Hover / open paint and the coarse-pointer 44 `::before` hit
// floor come from the atom (`styles/components/atoms.css`); on a fine pointer
// the hit box is the 36px paint.
//
// FRE-1362: through the whole ≤1024 range the pill collapses to a 36px
// icon-only circle, and it widens to its 200px placeholder form only past the
// design's OWN >1024 cut — not Tailwind's `lg` (min-width:1024px), which would
// already expand it AT 1024, one pixel inside the 721–1024 tablet band the
// design keeps compact. `.city-header-search` and the placeholder / key-cap
// classes (`styles/components/nav-lockup.css`) own that flip.
const SEARCH_CONTROL = 'city-header-search shrink-0 text-fg-3 hover:text-fg-1';

const SEARCH_KEY_CAP =
  'city-header-search-keycap rounded-[4px] border border-ds-border bg-black/30 px-1.5 py-0.5 font-mono text-micro tracking-[0.06em] text-fg-3';

/**
 * CityHeaderSearch — the `'use client'` search trigger island for the
 * marketing-city global-nav bar (FRE-873).
 *
 * Wires the DS `o-globalnav__search` affordance to the single global
 * `SearchSheet` store (`useSearchSheet().setOpen(true, { trigger: 'button' })`).
 * The sheet itself is mounted exactly once at the root by `SearchSheetMount`
 * (`app/_components/SearchSheetMount.tsx`) inside `app/providers.tsx`, so every
 * entry point — this button, ⌘K, and the bare `/` shortcut — converges on the
 * same module-scoped state (see `apps/web/CLAUDE.md` › Global nav surface).
 *
 * Extracted from the otherwise server-default `CityHeader` so the header tree
 * is not forced into the client bundle — mirrors the existing `WayfinderSheet`
 * and `CityNavMenu` island pattern.
 */
export function CityHeaderSearch({
  cityName,
  className,
}: CityHeaderSearchProps): React.ReactElement {
  const { setOpen } = useSearchSheet();
  const isMac = useIsMacPlatform();
  const searchPlaceholder = formatSearchCityPlaceholder(cityName);

  const handleClick = React.useCallback(() => {
    setOpen(true, { trigger: 'button' });
  }, [setOpen]);

  return (
    <GlassControl
      shape="pill"
      size="nav"
      onClick={handleClick}
      aria-label={isMac ? 'Open search (Command K)' : 'Open search (Control K)'}
      className={cn(SEARCH_CONTROL, className)}
      data-testid="city-header-search"
    >
      <Search aria-hidden="true" className="size-3.5 shrink-0" strokeWidth={2} />
      <span className="city-header-search-placeholder flex-1 text-left text-caption tracking-[0.01em]">
        {searchPlaceholder}
      </span>
      <span aria-hidden="true" className={SEARCH_KEY_CAP}>
        {isMac ? '⌘K' : 'Ctrl K'}
      </span>
    </GlassControl>
  );
}
